// @ts-nocheck
import { ForwardRefExoticComponent, RefAttributes, forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { Avatar, Box, Chip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles'
import MuiTooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'

// project imports
import { MENU_OPEN, SET_MENU } from 'src/store/actions';
import { DefaultRootStateProps, LinkTarget, NavItemType } from 'src/types';
import { adminTokens } from 'src/views/common/adminTokens';
import PhosphorIcon from 'src/views/common/PhosphorIcon';

export interface NavItemProps {
    item: NavItemType;
    level: number;
}

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }: NavItemProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state: DefaultRootStateProps) => state.customization);
    const drawerOpen: boolean = useSelector((state: DefaultRootStateProps) => state.customization.opened)
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const active = customization.isOpen.findIndex((id) => id === item?.id) > -1;

    const iconColor = item.ai ? adminTokens.ai600 : active ? adminTokens.navy800 : adminTokens.gray500;
    const Icon: any = item?.icon!;
    const itemIcon = item?.iconName ? (
        <PhosphorIcon name={item.iconName} size={18} color={iconColor} />
    ) : item?.icon ? (
        <Icon stroke={1.2} width={20} height={20} />
    ) : (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: adminTokens.gray400 }} />
    );

    let itemTarget: LinkTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps: {
        component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
        href?: string;
        target?: LinkTarget;
    } = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url!} target={itemTarget} />) };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id: string) => {
        dispatch({ type: MENU_OPEN, id });
        matchesSM && dispatch({ type: SET_MENU, opened: false });
    };

    const LightTooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)(
        ({ theme }) => ({
            [`& .${tooltipClasses.tooltip}`]: {
                fontSize: 11,
                boxShadow: theme.shadows[1],
                color: 'rgba(0, 0, 0, 0.87)',
                backgroundColor: theme.palette.common.white
            }
        })
    )

    // active menu item on page load
    useEffect(() => {
        const current = window.location.pathname === item.url
        if (current) dispatch({ type: MENU_OPEN, id: item.id })
        // eslint-disable-next-line
    }, [])

    if (!drawerOpen) {
        return (
            <LightTooltip placement='right' title={item.title}>
                <Box {...listItemProps} sx={{
                    marginY: '2px',
                    height: '40px',
                    width: '40px',
                    borderRadius: adminTokens.radiusMd,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: active ? adminTokens.navy800 : adminTokens.gray600,
                    backgroundColor: active ? adminTokens.gray100 : 'transparent',
                    '&:hover': { backgroundColor: adminTokens.gray100, color: adminTokens.navy800 }
                }}
                    onClick={() => itemHandler(item.id!)}
                >
                    {itemIcon}
                </Box>
            </LightTooltip>
        )
    }
    return (
        <Box
            {...listItemProps}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: 40,
                borderRadius: adminTokens.radiusMd,
                marginBottom: '2px',
                padding: level === 1 ? '0 12px' : `0 12px 0 ${level * 20}px`,
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? 0.45 : 1,
                textDecoration: 'none',
                fontFamily: adminTokens.fontSans,
                fontSize: adminTokens.textBase,
                fontWeight: active ? 600 : 500,
                color: active ? adminTokens.navy800 : adminTokens.gray600,
                backgroundColor: active ? adminTokens.gray100 : 'transparent',
                '&:hover': !item.disabled ? { backgroundColor: adminTokens.gray100, color: adminTokens.navy800 } : {},
            }}
            onClick={() => !item.disabled && itemHandler(item.id!)}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: 20, height: 20 }}>
                {itemIcon}
            </Box>
            <Typography
                component="span"
                sx={{ flex: 1, minWidth: 0, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', fontFamily: 'inherit' }}
                noWrap
            >
                {item.title}
            </Typography>
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </Box>
    );
};

export default NavItem;
